import React from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, BarChart3, BookOpen, Building2, ClipboardCheck, CreditCard, History, Mail, MessageCircle, Plug, Shield, Sparkles, UserCheck, Users, UserCog, UserRoundCheck, ListTodo } from "lucide-react";

type SuperAdminTabsNavigationProps = {
  activeTab: string;
  onValueChange: (value: string) => void;
  salonCount: number;
  leadsCount: number;
  pendingChannelCount: number;
};

const sections = [
  { value: "salons", label: "Salões", icon: Building2 },
  { value: "users", label: "Usuários", icon: Users },
  { value: "access", label: "Acessos", icon: Shield },
  { value: "platform-access", label: "Equipe Plataforma", icon: UserCog },
  { value: "success", label: "Sucesso", icon: UserRoundCheck },
  { value: "execution", label: "Execução", icon: ListTodo },
  { value: "health", label: "Saúde", icon: Activity },
  { value: "billing", label: "Planos e Cobranças", icon: CreditCard },
  { value: "onboarding", label: "Onboarding", icon: ClipboardCheck },
  { value: "reports", label: "Relatórios", icon: BarChart3 },
  { value: "integrations", label: "Integrações", icon: Plug },
  { value: "clients", label: "Clientes", icon: UserCheck },
  { value: "leads", label: "Leads", icon: Mail },
  { value: "channels", label: "Canais", icon: MessageCircle },
  { value: "metrics", label: "Métricas", icon: BarChart3 },
  { value: "features", label: "Funcionalidades", icon: Sparkles },
  { value: "audit", label: "Auditoria", icon: History },
  { value: "tutorials", label: "Tutoriais", icon: BookOpen },
] as const;

const sectionGroups = [
  { label: "Operação", values: ["salons", "success", "execution", "health", "onboarding"] },
  { label: "Receita e crescimento", values: ["billing", "clients", "leads", "reports", "metrics"] },
  { label: "Produto e integrações", values: ["features", "integrations", "channels", "tutorials"] },
  { label: "Governança", values: ["users", "access", "platform-access", "audit"] },
] as const;

export function SuperAdminTabsNavigation({ activeTab, onValueChange, salonCount, leadsCount, pendingChannelCount }: SuperAdminTabsNavigationProps) {
  const labelFor = (section: typeof sections[number]) => {
    if (section.value === "salons") return `Salões (${salonCount})`;
    if (section.value === "leads") return `Leads (${leadsCount})`;
    if (section.value === "channels") return `Canais${pendingChannelCount > 0 ? ` (${pendingChannelCount})` : ""}`;
    return section.label;
  };

  const sectionFor = (value: string) => sections.find((section) => section.value === value)!;

  return <>
    <div className="mb-4 block md:hidden">
      <Select value={activeTab} onValueChange={onValueChange}>
        <SelectTrigger className="w-full"><SelectValue placeholder="Selecione a seção" /></SelectTrigger>
        <SelectContent>{sectionGroups.map((group) => <SelectGroup key={group.label}><SelectLabel>{group.label}</SelectLabel>{group.values.map((value) => { const section = sectionFor(value); return <SelectItem key={section.value} value={section.value}>{labelFor(section)}</SelectItem>; })}</SelectGroup>)}</SelectContent>
      </Select>
    </div>
    <TabsList className="mb-6 hidden h-auto w-full grid-cols-2 gap-3 rounded-xl bg-transparent p-0 lg:grid lg:grid-cols-4">
      {sectionGroups.map((group) => (
        <div key={group.label} className="rounded-xl border bg-card p-2 shadow-sm">
          <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</p>
          <div className="flex flex-wrap gap-1">
            {group.values.map((value) => {
              const section = sectionFor(value);
              const Icon = section.icon;
              return <TabsTrigger key={section.value} value={section.value} className="h-8 flex-none px-2 text-xs" onClick={() => onValueChange(section.value)}><Icon className="mr-1.5 h-3.5 w-3.5" />{labelFor(section)}{section.value === "channels" && pendingChannelCount > 0 && <span className="ml-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] text-white">{pendingChannelCount}</span>}</TabsTrigger>;
            })}
          </div>
        </div>
      ))}
    </TabsList>
  </>;
}
